/**
 * Strict Zod schemas for all GitHub-backed JSON content.
 * Validation happens at load time; anything failing validation is
 * filtered out and reported (never crashes the app).
 *
 * v23 update: unified multilingual fields (AR / EN / FR) across movie,
 * series, and episode schemas — the dashboard now edits and the JSON
 * files now store the same shape:
 *
 *   - Movies / Series: title / title_en / title_fr, description /
 *     description_en / description_fr, genres_ar / genres / genres_fr
 *   - Episodes: `title` accepts either the legacy pipe string
 *     ("AR | EN | FR") OR an object { ar, en, fr } and the loader keeps
 *     them interchangeable via `parseEpisodeTitle`.
 */
import { z } from "zod";

export const videoSourceSchema = z.object({
  type: z.enum(["mp4", "m3u8", "external"]),
  /** Opaque identifier sent to the Cloudflare Worker. NEVER an origin URL. */
  source_id: z.string().min(1),
  /** Optional origin URL — kept server-side only; client code MUST ignore it. */
  source_url: z.string().url().optional(),
  protected: z.literal(true).default(true),
});

export const downloadLinkSchema = z.object({
  quality: z.enum(["480p", "720p", "1080p", "4K"]),
  size: z.string().default(""),
  server: z.string().default("Server 1"),
  url: z.string().min(1),
  /** "watch" = streaming link, "download" = direct download. Defaults to "download". */
  kind: z.enum(["watch", "download"]).optional(),
});


export const movieSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  title_en: z.string().optional(),
  title_fr: z.string().optional(),
  original_title: z.string().min(1),
  description: z.string().default(""),
  description_en: z.string().optional(),
  description_fr: z.string().optional(),
  year: z.number().int().min(1880).max(2100),
  duration: z.string().default(""),
  duration_minutes: z.number().int().positive().optional(),
  release_date: z.string().optional(),
  genres: z.array(z.string()).default([]),
  genres_ar: z.array(z.string()).optional(),
  genres_fr: z.array(z.string()).optional(),
  country: z.string().optional(),
  rating: z.number().min(0).max(10).default(0),
  cast: z.array(z.string()).default([]),
  director: z.string().default(""),
  poster: z.string().url(),
  backdrop: z.string().url(),
  trailer_url: z.string().url().optional(),
  tags: z.array(z.string()).default([]),
  is_trending: z.boolean().optional(),
  is_new: z.boolean().optional(),
  video_source: videoSourceSchema,
  watch_url: z.string().url().optional(),
  watch_token: z.string().optional(),
  downloads: z.array(downloadLinkSchema).default([]),
  paragraphs: z.array(z.string()).optional(),
  article: z.array(z.unknown()).optional(),
});
export type ContentMovie = z.infer<typeof movieSchema>;


export const seriesInfoSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  title_en: z.string().optional(),
  title_fr: z.string().optional(),
  original_title: z.string().min(1),
  description: z.string().default(""),
  description_en: z.string().optional(),
  description_fr: z.string().optional(),
  year: z.number().int().min(1880).max(2100).optional(),
  first_air_date: z.string().optional(),
  last_air_date: z.string().optional(),
  status: z.string().optional(),
  status_en: z.string().optional(),
  status_fr: z.string().optional(),
  genres: z.array(z.string()).default([]),
  genres_ar: z.array(z.string()).optional(),
  genres_fr: z.array(z.string()).optional(),
  country: z.string().optional(),
  rating: z.number().min(0).max(10).default(0),
  cast: z.array(z.string()).default([]),
  director: z.string().default(""),
  poster: z.string().url(),
  backdrop: z.string().url(),
  trailer_url: z.string().url().optional(),
  tags: z.array(z.string()).default([]),
  is_trending: z.boolean().optional(),
  is_new: z.boolean().optional(),
  seasons_count: z.number().int().positive(),
  watch_url: z.string().url().optional(),
  watch_token: z.string().optional(),
  downloads: z.array(downloadLinkSchema).default([]),
  paragraphs: z.array(z.string()).optional(),
  article: z.array(z.unknown()).optional(),
});
export type ContentSeriesInfo = z.infer<typeof seriesInfoSchema>;

/**
 * Episode title — a single field that accepts:
 *   - string  (legacy or pipe-separated: "AR | EN | FR")
 *   - object  ({ ar, en, fr })
 *
 * Read/write helpers live in `src/lib/episode-title.ts`.
 */
export const episodeTitleSchema = z.union([
  z.string().min(1),
  z.object({
    ar: z.string().optional(),
    en: z.string().optional(),
    fr: z.string().optional(),
  }),
]);
export type EpisodeTitle = z.infer<typeof episodeTitleSchema>;

/**
 * A single quality-tagged watch source (رابط مشاهدة مع الجودة).
 * Each entry may carry its own pre-encrypted watch_token so the player
 * can play the right stream when the viewer picks a specific quality.
 */
export const watchQualitySchema = z.object({
  quality: z.string().min(1),
  // URL can be empty when the admin provides only a pre-encrypted token for
  // a quality. The player can play token-only qualities without exposing the
  // raw source URL, and the dashboard validates non-empty URLs separately.
  url: z.string().default(""),
  token: z.string().optional(),
  label: z.string().optional(),
});
export type WatchQuality = z.infer<typeof watchQualitySchema>;

export const episodeSchema = z.object({
  episode_number: z.number().int().positive(),
  title: episodeTitleSchema,
  duration: z.string().default(""),
  thumbnail: z.string().url().optional(),
  video_source: videoSourceSchema,
  watch_url: z.string().url().optional(),
  watch_token: z.string().optional(),
  /**
   * Optional per-quality watch links. When present, the player shows a
   * quality picker (buttons per quality) after clicking "Watch". Each
   * quality can carry its own pre-encrypted `token` for that stream.
   */
  watch_urls: z.array(watchQualitySchema).optional(),
  /**
   * Optional direct download URL. When absent, the /download page will
   * try to extract quality variants from the video source (Rumble /
   * Vimeo / MP4).
   */
  download_url: z.string().optional(),
});

export type ContentEpisode = z.infer<typeof episodeSchema>;

export const seasonSchema = z.object({
  season: z.number().int().positive(),
  episodes: z.array(episodeSchema).min(1),
});
export type ContentSeason = z.infer<typeof seasonSchema>;

export const categoriesSchema = z.object({
  version: z.number().int().positive(),
  manual: z
    .array(
      z.object({
        slug: z.string().min(1),
        ar: z.string().min(1),
        en: z.string().min(1),
      }),
    )
    .default([]),
});
export type ContentCategories = z.infer<typeof categoriesSchema>;

export const homepageSchema = z.object({
  version: z.number().int().positive(),
  sections: z.array(
    z.object({
      id: z.string().min(1),
      key: z.enum([
        "trending",
        "latest_movies",
        "latest_series",
        "top_rated",
        "recommended",
      ]),
      title_ar: z.string().min(1),
      title_en: z.string().min(1),
      limit: z.number().int().positive().max(60).default(12),
    }),
  ),
});
export type ContentHomepage = z.infer<typeof homepageSchema>;

/** Safely parse + log a single content document. */
export function safeParse<T extends z.ZodTypeAny>(
  schema: T,
  raw: unknown,
  origin: string,
): z.infer<T> | null {
  const r = schema.safeParse(raw);
  if (r.success) return r.data;
  // eslint-disable-next-line no-console
  console.warn(`[content] invalid document at ${origin}:`, r.error.flatten());
  return null;
}
